import * as zod from 'zod';

import { CartesianPose, ZERO_POSE } from '@sb/geometry';
import { CameraIntegration } from '@sb/integrations/types/cameraTypes';
import { ArmJointPositions, TCPOffsetOption } from '@sb/motion-planning';

import { PalletBoxLayerPattern, PalletBoxType } from './PalletizingTypes';

export namespace Space {
  export const Position = zod.object({
    name: zod.string().optional(),
    jointAngles: ArmJointPositions,
    pose: CartesianPose,
    tcpOption: TCPOffsetOption.optional(),
    gripperID: zod.string().optional(),
    wristPose: CartesianPose.optional(),
  });

  export type Position = zod.infer<typeof Position>;

  // Lightweight warning annotation stored on space items (e.g., unreachable positions).
  export const Warning = zod.object({
    kind: zod.enum(['unreachable']),
    positionIndices: zod.array(zod.number()).optional(),
    message: zod.string().optional(),
  });

  export type Warning = zod.infer<typeof Warning>;

  export const CalibratedPosition = zod.object({
    rawPosition: Position,
    calibratedPosition: Position.nullable().default(null),
    offset: Position.nullable().default(null),
  });

  export type CalibratedPosition = zod.infer<typeof CalibratedPosition>;

  const ItemBase = zod.object({
    description: zod.string(),
    id: zod.string(),
    kind: zod.string(),
    name: zod.string(),
    positions: zod.array(Position).default([]),
    global: zod.boolean().default(false).optional(),
    anchoredToID: zod.string().optional(),
    warnings: zod.array(Warning).optional(),
    // Persistent per-item flag: when true, this item's visualization is shown
    // in the Move tab whenever its routine is loaded (see the move-tab
    // routine-visualization feature). Optional + default false so existing
    // stored items parse unchanged.
    showInMoveTab: zod.boolean().default(false).optional(),
  });

  export const FreeformPositionList = ItemBase.extend({
    kind: zod.literal('freeformPositionList'),
  });

  export type FreeformPositionList = zod.infer<typeof FreeformPositionList>;

  export const SinglePosition = ItemBase.extend({
    kind: zod.literal('singlePosition'),
    positions: zod.array(Position).max(1).default([]),
  });

  export type SinglePosition = zod.infer<typeof SinglePosition>;

  export const GridPositionList = ItemBase.extend({
    cornerA: Position.nullable().default(null),
    cornerB: Position.nullable().default(null),
    cornerC: Position.nullable().default(null).optional(),
    cornerD: Position.nullable().default(null).optional(),
    // `.catch` rather than `.default`: a stored `null` (what a `NaN` count
    // serializes to) would otherwise fail the whole item's parse, and callers
    // skip items that fail to parse — losing the grid and every position in it.
    // Falling back to the default keeps the item and its positions intact.
    numColumns: zod.number().catch(3),
    numRows: zod.number().catch(3),
    kind: zod.literal('gridPositionList'),
  });

  export type GridPositionList = zod.infer<typeof GridPositionList>;

  export const VisionPositionList = ItemBase.extend({
    kind: zod.literal('visionPositionList'),
    planeID: zod.string().optional(),
  });

  export type VisionPositionList = zod.infer<typeof VisionPositionList>;

  export const AccuracyCalibrationEntry = zod.object({
    rawPose: CartesianPose,
    calibratedPose: CartesianPose,
    offset: CartesianPose,
  });

  export type AccuracyCalibrationEntry = zod.infer<
    typeof AccuracyCalibrationEntry
  >;

  export const CameraAccuracyCalibration = zod.object({
    name: zod.string(), // Calibration name
    equipmentId: zod.string(), // Feathers equipment ID
    extrinsicsId: zod.string(), // Extrinsics ID: pins down cameraId, intrinsicsId, etc.

    calibrationHomePosition: Position.optional(),
    calibrationEntries: AccuracyCalibrationEntry.array().default([]),
  });

  export type CameraAccuracyCalibration = zod.infer<
    typeof CameraAccuracyCalibration
  >;

  export const Plane = ItemBase.extend({
    kind: zod.literal('plane'),
    capturedWithCameraKind: zod.string().optional(),

    /**
     * Accuracy calibrations for external cameras/camera-bot-cpp
     */
    cameraAccuracyCalibrations: zod
      .record(zod.string(), CameraAccuracyCalibration)
      .default({}),

    /**
     * Relevant for wrist camera accuracy calibration
     */
    calibrationHomePosition: Position.optional(),
    calibrationEntries: AccuracyCalibrationEntry.array().default([]),
    calibrationCameraSettings: CameraIntegration.default({
      kind: 'wristCamera',
    }),

    /**
     * Set when the plane was created via the depth-camera detect flow;
     * routes editing back to the detect view (chessboard flows key off
     * `capturedWithCameraKind` instead).
     */
    detectedWithDepthCamera: zod
      .object({ cameraKind: zod.string() })
      .optional(),
  });

  export type Plane = zod.infer<typeof Plane>;

  export const EnvironmentObject = ItemBase.extend({
    kind: zod.literal('environmentObject'),
    fileURL: zod.string().nullable().default(null),
    fileName: zod.string().nullable().default(null),
    pose: CartesianPose.default(ZERO_POSE),
    scale: zod.number().optional(),
  });

  export type EnvironmentObject = zod.infer<typeof EnvironmentObject>;

  export const SafeHomePose = ItemBase.extend({
    kind: zod.literal('safeHomePose'),
  });

  export type SafeHomePose = zod.infer<typeof SafeHomePose>;

  export const LocalAccuracyCalibration = GridPositionList.extend({
    kind: zod.literal('localAccuracyCalibration'),
    calibrationEntries: Position.nullable().array().default([]),
    // `.catch` for the same reason as the grid counts above: these two persist
    // on every keystroke, so a `NaN` written before the input guards existed is
    // stored as `null`, and `.default` only substitutes for `undefined`. A
    // mis-scaled measurement is visible and re-editable; a dropped item is not.
    rowMeasurementMM: zod.number().catch(0),
    columnMeasurementMM: zod.number().catch(0),
  });

  export type LocalAccuracyCalibration = zod.infer<
    typeof LocalAccuracyCalibration
  >;

  export const PalletBoxes = ItemBase.extend({
    kind: zod.literal('palletBoxes'),
    positions: zod.array(Position).max(0).default([]),
    palletBaseIDs: zod.array(zod.string()).default([]),
    approachDistanceXYMM: zod.number().default(100),
    approachDistanceZMM: zod.number().default(0),
    boxTypes: zod.array(PalletBoxType).default([]),
    layerPatterns: zod.array(PalletBoxLayerPattern).default([]),
    layerOrder: zod.array(zod.string()).default([]),
    customPositions: zod
      .object({
        approachPositions: zod.record(zod.record(Position)),
        pickupPositions: zod.record(zod.record(Position)),
      })
      .optional(),
  });

  export type PalletBoxes = zod.infer<typeof PalletBoxes>;

  export const PalletBase = ItemBase.extend({
    kind: zod.literal('palletBase'),
    positions: zod.array(Position).max(0).default([]),
    lengthMM: zod.number().default(0),
    widthMM: zod.number().default(0),
    heightMM: zod.number().default(0),
    floorToPalletDistanceMM: zod.number().default(0),
    cornerToPosition: zod
      .union([zod.literal('left'), zod.literal('right')])
      .default('left'),
    cornerXMM: zod.number().default(0),
    cornerYMM: zod.number().default(0),
    cornerYawDegrees: zod.number().default(0),
  });

  export type PalletBase = zod.infer<typeof PalletBase>;

  export const Item = zod.discriminatedUnion('kind', [
    FreeformPositionList,
    GridPositionList,
    SinglePosition,
    VisionPositionList,
    Plane,
    EnvironmentObject,
    SafeHomePose,
    LocalAccuracyCalibration,
    PalletBoxes,
    PalletBase,
  ]);

  export type Item = zod.infer<typeof Item>;

  export type ItemKind = Item['kind'];

  export const AnchorableItem = zod.discriminatedUnion('kind', [
    FreeformPositionList,
    GridPositionList,
    SinglePosition,
    Plane,
  ]);

  export type AnchorableItem = zod.infer<typeof AnchorableItem>;

  export const AnchorTargetItem = zod.discriminatedUnion('kind', [
    SinglePosition,
    Plane,
    LocalAccuracyCalibration,
  ]);

  export type AnchorTargetItem = zod.infer<typeof AnchorTargetItem>;

  /**
   * Migrates old PalletBoxes data from palletBaseID (string) to palletBaseIDs (array of string)
   * and from approachDistanceMM to approachDistanceXYMM
   * This handles backward compatibility when parsing space items from the database
   */
  export function migratePalletBoxesData(data: any): any {
    if (data?.kind === 'palletBoxes') {
      const migrated = { ...data };

      // Migrate palletBaseID to palletBaseIDs
      if ('palletBaseID' in data && !('palletBaseIDs' in data)) {
        migrated.palletBaseIDs = [data.palletBaseID];
        delete migrated.palletBaseID;
      }

      // Migrate approachDistanceMM to approachDistanceXYMM
      if ('approachDistanceMM' in data) {
        if (!('approachDistanceXYMM' in data)) {
          migrated.approachDistanceXYMM = data.approachDistanceMM;
        }

        delete migrated.approachDistanceMM;
      }

      // Ensure approachDistanceZMM exists with default value
      if (!('approachDistanceZMM' in data)) {
        migrated.approachDistanceZMM = 0;
      }

      return migrated;
    }

    return data;
  }
}
